// @owners { team: patients }
// Central location to manage all routing concerns for the onboarding flow
import { TypescriptUtils } from '~shared/TypescriptUtils';
export const WEB_LANDING_PAGE = 'WEB_LANDING_PAGE';
export const VERIFY = 'VERIFY';
export const MEDICAL_INFO = 'MEDICAL_INFO';
export const PHI_AUTHORIZATION = 'PHI_AUTHORIZATION';
export const INSURANCE_FOUND = 'INSURANCE_FOUND';
export const INSURANCE_INPUT = 'INSURANCE_INPUT';
export const INSURANCE_INPUT_MANUAL = 'INSURANCE_INPUT_MANUAL';
export const INSURANCE_INPUT_PHOTO = 'INSURANCE_INPUT_PHOTO';
export const CREATE_ACCOUNT = 'CREATE_ACCOUNT';
export const FERTILITY_SUPPORT = 'FERTILITY_SUPPORT';
export const ONBOARD_SUCCESS = 'ONBOARD_SUCCESS';
export const HOME = 'HOME';

export const ONBOARD_STATE_INSURANCE_FOUND = 'provider_insurance_found';
export const ONBOARD_STATE_INSURANCE_INPUT = 'provider_insurance_input';
export const ONBOARD_STATE_MEDICAL_INFO = 'provider_medical_info';
export const ONBOARD_STATE_CREATE_ACCOUNT = 'provider_create_account';
export const ONBOARD_STATE_DONE = 'done';
export const ONBOARD_STATE_PHI_AUTHORIZATION = 'provider_phi_authorization';
export const ONBOARD_STATE_FERTILITY_SUPPORT = 'provider_fertility_support';

export const ONBOARD_STATE_TO_ROUTE: Record<string, Route> = {
  [ONBOARD_STATE_INSURANCE_FOUND]: INSURANCE_FOUND,
  [ONBOARD_STATE_INSURANCE_INPUT]: INSURANCE_INPUT,
  [ONBOARD_STATE_MEDICAL_INFO]: MEDICAL_INFO,
  [ONBOARD_STATE_PHI_AUTHORIZATION]: PHI_AUTHORIZATION,
  [ONBOARD_STATE_CREATE_ACCOUNT]: CREATE_ACCOUNT,
  [ONBOARD_STATE_FERTILITY_SUPPORT]: FERTILITY_SUPPORT,
};

export type Route =
  | typeof WEB_LANDING_PAGE
  | typeof VERIFY
  | typeof INSURANCE_FOUND
  | typeof INSURANCE_INPUT
  | typeof INSURANCE_INPUT_MANUAL
  | typeof INSURANCE_INPUT_PHOTO
  | typeof MEDICAL_INFO
  | typeof PHI_AUTHORIZATION
  | typeof CREATE_ACCOUNT
  | typeof FERTILITY_SUPPORT
  | typeof ONBOARD_SUCCESS
  | typeof HOME;

export type RouteDecisionParams = {
  hasInsurance?: boolean;
  editingInsuranceID?: number | null;
};

type RouteDecision = {
  next: (params: RouteDecisionParams, desiredRoute?: Route | null) => Route | null | undefined;
  previous: (params: RouteDecisionParams) => Route | null | undefined;
  currentStep: () => number;
};

type Routing = Record<Route, RouteDecision | undefined>;

const onboardingRouting: Routing = {
  [WEB_LANDING_PAGE]: undefined,
  [VERIFY]: {
    next: (_, desiredRoute) => {
      // When a patient has left the onboarding flow midway and verifies again,
      // send them to where they left off
      return desiredRoute ? desiredRoute : MEDICAL_INFO;
    },
    previous: () => null,
    currentStep: () => 0,
  },
  [MEDICAL_INFO]: {
    next: () => PHI_AUTHORIZATION,
    previous: () => null,
    currentStep: () => 1,
  },
  [PHI_AUTHORIZATION]: {
    next: ({ hasInsurance }) => {
      return hasInsurance ? INSURANCE_FOUND : INSURANCE_INPUT;
    },
    previous: () => MEDICAL_INFO,
    currentStep: () => 2,
  },
  [INSURANCE_FOUND]: {
    next: (_, desiredRoute) => {
      // When a patient clicks "Add other insurance", INSURANCE_INPUT is passed into desiredRoute
      return desiredRoute ? desiredRoute : CREATE_ACCOUNT;
    },
    previous: () => {
      return PHI_AUTHORIZATION;
    },
    currentStep: () => 3,
  },
  [INSURANCE_INPUT]: {
    next: ({ editingInsuranceID }, desiredRoute) => {
      // When a patient inputs their insurance manually or by photo, a desiredRoute is passed in
      // the patient cannot advance from the insurance input screen and must go back to INSURANCE_FOUND
      // or add an insurance via manual entry or photo upload
      const defaultNextRoute = editingInsuranceID ? INSURANCE_FOUND : CREATE_ACCOUNT;
      return desiredRoute ?? defaultNextRoute;
    },
    previous: ({ hasInsurance }) => {
      // If the patient has insurance, go back to INSURANCE_FOUND. Otherwise, route to INSURANCE_INPUT
      return hasInsurance ? INSURANCE_FOUND : PHI_AUTHORIZATION;
    },
    currentStep: () => 3,
  },
  [INSURANCE_INPUT_MANUAL]: {
    next: (_, desiredRoute) => {
      // When a patient inputs their insurance manually or by photo, a desiredRoute is passed in
      return desiredRoute ? desiredRoute : INSURANCE_FOUND;
    },
    previous: ({ editingInsuranceID }) => {
      // If the user is editing an insurance, go back to INSURANCE_FOUND. Otherwise, route to INSURANCE_INPUT
      return editingInsuranceID ? INSURANCE_FOUND : INSURANCE_INPUT;
    },
    currentStep: () => 3,
  },
  [INSURANCE_INPUT_PHOTO]: {
    next: (_, desiredRoute) => {
      // When a patient inputs their insurance manually or by photo, a desiredRoute is passed in
      return desiredRoute ? desiredRoute : INSURANCE_FOUND;
    },
    previous: ({ editingInsuranceID }) => {
      // If the user is editing an insurance, go back to INSURANCE_FOUND. Otherwise, route to INSURANCE_INPUT
      return editingInsuranceID ? INSURANCE_FOUND : INSURANCE_INPUT;
    },
    currentStep: () => 3,
  },
  [CREATE_ACCOUNT]: {
    next: (_, desiredRoute) => {
      // fertility and progyny patients will be routed to fertility support
      return desiredRoute ? desiredRoute : ONBOARD_SUCCESS;
    },
    previous: ({ hasInsurance }) => {
      return hasInsurance ? INSURANCE_FOUND : INSURANCE_INPUT;
    },
    currentStep: () => 4,
  },
  [FERTILITY_SUPPORT]: {
    next: () => ONBOARD_SUCCESS,
    previous: () => CREATE_ACCOUNT,
    currentStep: () => 4, // not shown in progress bar
  },
  // when mobile web, renders app drawer "How do you want to use Alto?"
  // when progyny patient, renders Download the Alto app
  [ONBOARD_SUCCESS]: {
    next: () => HOME,
    previous: () => null,
    currentStep: () => 4, // not shown in progress bar
  },
  [HOME]: {
    next: () => null,
    previous: () => null,
    currentStep: () => 0,
  },
};

export function nextOnboardingRoute({
  params = {},
  currentRoute,
  desiredRoute,
}: {
  params?: RouteDecisionParams;
  currentRoute: Route;
  desiredRoute?: Route;
}): Route {
  return onboardingRouting[currentRoute]?.next(params, desiredRoute) || currentRoute;
}

export function previousOnboardingRoute({
  params = {},
  currentRoute,
}: {
  params?: RouteDecisionParams;
  currentRoute: Route;
}): Route | null | undefined {
  return onboardingRouting[currentRoute]?.previous(params);
}

export function totalSteps(): number {
  return Math.max(
    ...TypescriptUtils.objectKeys(onboardingRouting).map((key) => onboardingRouting[key]?.currentStep() ?? 0),
  );
}

export function currentStep({ route }: { route: Route | null | undefined }): number {
  return (route && onboardingRouting[route]?.currentStep()) || 0;
}
